/**
 * Formatting per Non-Functional.md: Intl everywhere, NOK default, tabular money with the currency always
 * shown and negatives in parentheses, dates in the tenant timezone (Europe/Oslo by default), ISO in APIs.
 */
import type { Orgnr } from '@autho/core';
import { formatOrgnr as coreFormatOrgnr, dayNumber, localDate, parseOrgnr } from '@autho/core';
import type { Locale } from './locales.ts';

export const DEFAULT_TIMEZONE = 'Europe/Oslo';

/**
 * Money arrives as a decimal string (numeric(18,2)) so no precision is lost before display. Two decimals unless the
 * figure is a cost that can be smaller than an øre — a model call, a backfill estimate — where `maxFractionDigits`
 * keeps it from reading as nothing: *NOK 0.0042*, not *NOK 0.00*.
 */
export function formatMoney(
  amount: string | number,
  currency: string,
  locale: Locale,
  options: { maxFractionDigits?: number } = {},
): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) throw new Error('amount is not a decimal number');
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    currencySign: 'accounting',
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(2, options.maxFractionDigits ?? 2),
  }).format(value);
}

/** A share as a percentage, `0.125` → *12.5 %* / *12,5 %*; at most two decimals, as a stake is registered. */
export function formatPercent(fraction: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(
    fraction,
  );
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatDate(
  iso: string | Date,
  locale: Locale,
  timeZone = DEFAULT_TIMEZONE,
): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone }).format(date);
}

/**
 * The calendar day a moment falls on where the person is, as `YYYY-MM-DD`: what a date input holds and what a day
 * *still to come* is measured against. Not a display format; the screens write a day with {@link formatCalendarDate}.
 */
export function isoDay(at: Date, timeZone = DEFAULT_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** A calendar month (`2026-10`), named in the locale's words: `October 2026`, `oktober 2026`. No timezone. */
export function formatMonth(isoMonth: string, locale: Locale): string {
  const match = /^(\d{4})-(\d{2})$/.exec(isoMonth);
  if (!match) throw new Error('expected YYYY-MM');
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}

/** Date-only values (e.g. registration date `2021-03-16`) are calendar dates with no timezone. */
export function formatCalendarDate(isoDate: string, locale: Locale): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) throw new Error('expected YYYY-MM-DD');
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

export function formatDateTime(
  iso: string | Date,
  locale: Locale,
  timeZone = DEFAULT_TIMEZONE,
): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(date);
}

/**
 * From one moment to another, written once: *15 Oct 2026, 16:27–17:27*, the date not repeated when both ends fall
 * on the same day in `timeZone`. A calendar's event is the case; anything with a start and an end is.
 */
export function formatDateTimeRange(
  start: string | Date,
  end: string | Date,
  locale: Locale,
  timeZone = DEFAULT_TIMEZONE,
): string {
  const from = typeof start === 'string' ? new Date(start) : start;
  const to = typeof end === 'string' ? new Date(end) : end;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).formatRange(from, to);
}

const DIVISIONS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 30],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
];

/** Days since 1 January 1970 of the calendar date a moment falls on in `timeZone`. */
const calendarDay = (at: Date, timeZone: string): number => dayNumber(localDate(at, timeZone));

/**
 * **Norwegian says *dager*, not *døgn*** (Thomas, 2026-10-09). ICU's bokmål writes a count of days as *døgn* —
 * *for 3 døgn siden*, *om 3 døgn* — which is the word for twenty-four hours on the clock, and every day counted here
 * is a calendar day. Only that noun changes, and only in the parts ICU marks as words: *i dag*, *i går* and
 * *i forgårs*, the number with its own spacing, and every other unit are still `Intl`'s. ICU writes the noun only
 * for three days or more, so it is always the plural.
 */
function relativeTime(locale: Locale, value: number, unit: Intl.RelativeTimeFormatUnit): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (unit !== 'day' || locale !== 'nb-NO') return rtf.format(value, unit);
  return rtf
    .formatToParts(value, unit)
    .map((part) => (part.type === 'literal' ? part.value.replace('døgn', 'dager') : part.value))
    .join('');
}

/**
 * A moment as *3 hours ago*, *yesterday*, *2 months ago*. **Days are calendar days, where the company is**:
 * *yesterday* is a date, and thirty hours before 05:00 is the day before yesterday. Rounding elapsed hours to
 * days said *yesterday* for it.
 */
export function formatRelative(
  iso: string | Date,
  locale: Locale,
  now: Date = new Date(),
  timeZone = DEFAULT_TIMEZONE,
): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  let delta = (date.getTime() - now.getTime()) / 1000;
  for (const [unit, size] of DIVISIONS) {
    if (Math.abs(delta) < size)
      return unit === 'day'
        ? relativeTime(locale, calendarDay(date, timeZone) - calendarDay(now, timeZone), 'day')
        : relativeTime(locale, Math.round(delta), unit);
    delta /= size;
  }
  /* c8 ignore next */
  return relativeTime(locale, Math.round(delta), 'year');
}

/**
 * **Whole days ago, in words, with the language's own plural** (Thomas, 2026-10-08: *a file uploaded today says
 * "uploaded 1 days ago"*). `days` is calendar days, counted by whoever owns the clock; `Intl` writes *today*,
 * *yesterday* and the plural, so no sentence carries a number it cannot inflect — and in Norwegian the days are
 * *dager* (`relativeTime`).
 */
export function formatDaysAgo(days: number, locale: Locale): string {
  if (!Number.isInteger(days) || days < 0)
    throw new Error('days ago is a whole number of days, and not negative');
  return relativeTime(locale, -days, 'day');
}

/** `123 456 789` for valid org numbers; anything else is returned unchanged rather than guessed at. */
export function formatOrgnr(value: string): string {
  const parsed: Orgnr | null = parseOrgnr(value);
  return parsed ? coreFormatOrgnr(parsed) : value;
}
